import { useEffect, useState } from "react";
import { Dialog, DialogCancelButton } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { TextInput, TextareaInput } from "@/components/ui/Field";
import { Alert } from "@/components/ui/Alert";
import {
  createUnavailability,
  updateUnavailability,
  type PlanungRow,
  type PlanungUnavailability,
} from "@/lib/montageplanung";
import { rowLabelFull } from "./displayLabels";
import { MP_DIALOG_Z_INDEX_CLASS } from "./layoutConstants";

export interface UnavailabilityDialogProps {
  open: boolean;
  onClose: () => void;
  row: PlanungRow | null;
  existing: PlanungUnavailability | null;
  defaultFrom: string;
  defaultTo: string;
  onSaved: () => void;
}

export function UnavailabilityDialog({
  open,
  onClose,
  row,
  existing,
  defaultFrom,
  defaultTo,
  onSaved,
}: UnavailabilityDialogProps) {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (existing) {
      setDateFrom(existing.date_from.slice(0, 10));
      setDateTo(existing.date_to.slice(0, 10));
      setNote(existing.note || "");
    } else {
      setDateFrom(defaultFrom);
      setDateTo(defaultTo);
      setNote("");
    }
  }, [open, existing, defaultFrom, defaultTo]);

  async function handleSave() {
    if (!row) return;
    if (dateTo < dateFrom) {
      setError("Das Enddatum darf nicht vor dem Startdatum liegen.");
      return;
    }
    setSaving(true);
    setError(null);
    const payload = {
      row_id: row.id,
      date_from: dateFrom,
      date_to: dateTo,
      note: note.trim() || null,
    };
    try {
      if (existing) {
        await updateUnavailability(existing.id, payload);
      } else {
        await createUnavailability(payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  const employeeLabel = row ? rowLabelFull(row) : "";

  const dirty = existing
    ? dateFrom !== existing.date_from.slice(0, 10) ||
      dateTo !== existing.date_to.slice(0, 10) ||
      note !== (existing.note || "")
    : dateFrom !== defaultFrom || dateTo !== defaultTo || note.trim() !== "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      dirty={dirty}
      title={existing ? "Nicht verfügbar bearbeiten" : "Nicht verfügbar eintragen"}
      size="sm"
      fullScreenMobile
      zIndexClass={MP_DIALOG_Z_INDEX_CLASS}
      footer={
        <>
          <DialogCancelButton onClose={onClose} disabled={saving} />
          <Button type="button" onClick={() => void handleSave()} disabled={saving}>
            {saving ? "Speichern…" : "Speichern"}
          </Button>
        </>
      }
    >
      <p className="mb-3 text-sm text-label-secondary">
        Für <strong className="text-label">{employeeLabel}</strong> — ohne Antragsworkflow, nur
        Planungsdarstellung (wie Wochenende).
      </p>
      {error && (
        <Alert variant="error" className="mb-3">
          {error}
        </Alert>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <TextInput
          label="Von"
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
        />
        <TextInput
          label="Bis"
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
        />
      </div>
      <TextareaInput
        label="Notiz (optional)"
        className="mt-3"
        rows={2}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="z. B. Rente, nur unregelmäßig im Einsatz"
      />
    </Dialog>
  );
}
